import { useEffect, useState } from 'react';
import { Button, Checkbox, CheckboxGroup, Spin, Text } from '../Ant';
import { useTranslation } from 'react-i18next';
import { useFetchUsers } from '../../hooks/useFetchUsers';
import { useTask } from '../../context/TaskContext';
import { useInView } from 'react-intersection-observer';
import { useQueryClient } from '@tanstack/react-query';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { ITask } from '../../interface/tasks';
import NoData from '../dashboard/noData';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { GeneralEmpty, Slash } from '../icons';
import { IUser } from '../../interface/user';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import { useDiscardDialog } from '../../hooks/useDiscardDialog';
import { isCriticalTaskFormChanged } from './isCriticalTaskFormChanged';
import CustomModal from '../informationPopup/customModal';

const PersonsListing = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { task, updateTask, initialTask, clearInitialTask } = useTask();
  const { setTitle } = usePage();
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    data: userList,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchUsers({
    pageSize: 10,
    sortBy: ['firstNames:ASC'],
    search: searchValue,
  });
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>(
    task?.otherResponsibleUsers?.map((entry) => entry.user) ?? [],
  );
  const selectedUserIds = selectedUsers.map((user) => user.id);
  const hasFetchFailed = !!error && userList.length === 0;

  const refreshUserList = () => {
    queryClient.removeQueries({ queryKey: ['users-list'] });
    return refetch();
  };

  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    promptDiscardIfChanged(isCriticalTaskFormChanged(task, initialTask, !!task?.id), () => {
      updateTask(null);
      clearInitialTask();
      goBack(undefined, 2);
    });
  };

  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && userList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('task:selectPersons')}
        showCross
      />,
    );
  }, []);
  // CheckboxGroup only reports the checkboxes it currently renders, so a search that hides an
  // already-selected user would otherwise silently deselect them.
  const handleSelectionChange = (values: unknown[]) => {
    const checkedIds = new Set(values as string[]);
    const visibleIds = new Set(userList.map((user: IUser) => user.id));
    const selectedOutOfView = selectedUsers.filter((user) => !visibleIds.has(user.id));
    const selectedInView = userList.filter((user: IUser) => checkedIds.has(user.id));
    setSelectedUsers([...selectedOutOfView, ...selectedInView]);
  };

  const handleSelectPerson = () => {
    updateTask({
      ...task,
      otherResponsibleUsers: selectedUsers.map((user) => ({ user })),
    } as ITask);

    goBackWithSelection();
  };

  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" data-testid="small-indicator" />;
    }
    return null;
  };
  return (
    <>
      <CollapsibleListLayout
        testId="persons-listing"
        contentGap="gutter"
        header={
          <SearchInputWithFilters
            autoFocus
            searchTitle={t('common:searchUsers')}
            handleSearch={setSearchValue}
            handleRefresh={refreshUserList}
          />
        }
      >
        {userList && userList.length > 0 && (
          <CheckboxGroup
            data-testid="persons-listing-checkbox-group"
            className="d-flex fd-column gap-10 persons-listing"
            value={selectedUserIds}
            onChange={handleSelectionChange}
          >
            {userList.map((option: IUser) => (
              <Checkbox
                key={option.id}
                value={option.id}
                className="w-full"
                data-testid={`person-checkbox-${option.id}`}
              >
                <Text $fontsize="13px" $weight={500} $color="title" $lineheight="20px">
                  {option.fullName}
                </Text>
              </Checkbox>
            ))}
          </CheckboxGroup>
        )}

        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {hasFetchFailed && (
          <div
            className="flex-grow d-flex justify-center items-center"
            role="status"
            aria-live="polite"
          >
            <NoData header={t('common:somethingWentWrongTryAgain')} icon={<GeneralEmpty />} />
          </div>
        )}
        {!hasFetchFailed &&
          (!userList || (Object.entries(userList).length === 0 && !isLoading)) && (
            <div
              className="flex-grow d-flex justify-center items-center"
              role="status"
              aria-live="polite"
            >
              <NoData
                header={t('common:noUsersFound')}
                subHeader={t('common:tryChangingSearchCriteria')}
                icon={<GeneralEmpty />}
              />
            </div>
          )}
        {isLoading ? <SkeletonOptionList /> : renderLoader()}
        <Button
          data-testid="select-persons-button"
          type="primary"
          disabled={selectedUserIds.length === 0}
          size="large"
          className="flex-shrink-0"
          onClick={handleSelectPerson}
        >
          {t('task:selectPersons')}
        </Button>
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default PersonsListing;
